import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { Building2, MousePointerClick, Store, Sparkles, Info } from 'lucide-react';
import { backofficeApi, formatNumber, formatDate } from '@kardume/services';
import { theme } from '@kardume/theme';
import type {
  ComboItem,
  MerchantInterestQuery,
  MerchantInterestSort,
} from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Card,
  CardHeader,
  CardBody,
  Table,
  TableContainer,
  Badge,
  Select,
  SearchableComboBox,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

/** Quantas barras cabem antes de os rótulos virarem um borrão. */
const MAX_BARS = 8;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  /* Iguala a altura dos controles: o combobox põe a borda no wrapper em volta
     do input e o select põe nele mesmo, e sem isto a linha de filtros fica
     escalonada. */
  select {
    box-sizing: border-box;
    height: 42px;
  }
`;

const FilterField = styled.div`
  flex: 1 1 200px;
  min-width: 180px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Muted = styled.span`
  color: ${(p) => p.theme.textMuted};
`;

const Numeric = styled.td`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const NumericHead = styled.th`
  text-align: right;
`;

const Note = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 1rem 0 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const PERIOD_OPTIONS = [
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '90', label: 'Últimos 90 dias' },
  { value: '365', label: 'Últimos 12 meses' },
];

const ORDER_OPTIONS: { value: MerchantInterestSort; label: string }[] = [
  { value: 'INTEREST', label: 'Ordenar por interesse total' },
  { value: 'CONTACTS', label: 'Ordenar por contatos' },
  { value: 'VIEWS', label: 'Ordenar por visualizações' },
  { value: 'SAVES', label: 'Ordenar por salvamentos' },
];

/**
 * Os dois eixos da relação com a plataforma são filtros separados, e não um
 * "estágio" único, porque não são níveis: uma empresa pode ter dono e não ser
 * parceira, e uma parceira antiga pode nunca ter reivindicado a página.
 */
const PARTNER_OPTIONS = [
  { value: '', label: 'Parceria: todas' },
  { value: 'true', label: 'Era parceira' },
  { value: 'false', label: 'Ainda não era parceira' },
];

const CLAIM_OPTIONS = [
  { value: '', label: 'Reivindicação: todas' },
  { value: 'true', label: 'Página tinha dono' },
  { value: 'false', label: 'Página sem dono' },
];

const toIsoDate = (date: Date) => date.toISOString().slice(0, 10);

const periodOf = (days: number) => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { from: toIsoDate(from), to: toIsoDate(to) };
};

const parseTriState = (raw: string): boolean | undefined =>
  raw === '' ? undefined : raw === 'true';

/** Cidade sem nome não é uma cidade chamada "-"; é a ausência do dado. */
const cityLabel = (city: string | null) => city ?? 'Sem cidade';

/**
 * A visão de dentro do interesse por empresa e por cidade (card 258, KU-05d).
 *
 * ## Que pergunta esta tela responde
 *
 * O painel do responsável responde "como vai a minha página?". Esta responde
 * **"para quem vale a pena ligar?"** - e é por isso que ela é outra tela, e não
 * o mesmo painel sem filtro. Ela é também a base do score do KU-29: o número
 * cru vem primeiro, com o time olhando, porque é olhando que se descobre o que
 * o score precisa ponderar.
 *
 * ## Por que "antes da parceria" tem coluna própria
 *
 * É o argumento comercial inteiro numa célula: uma empresa com 400 eventos
 * antes de existir qualquer relação conosco é a prova de que o guia já trabalha
 * por ela. Só existe porque o core guarda `partner` como fotografia do momento
 * do evento - resolvido por join com a empresa de hoje, esse número seria zero
 * para toda parceira.
 *
 * ## Dois carregamentos, de propósito
 *
 * O resumo (totais, cidades, categorias) e o ranking são chamadas separadas.
 * Paginar a lista não recarrega os gráficos, e um erro num deles não apaga o
 * outro da tela.
 */
const MerchantInterestPage: React.FC = () => {
  const [days, setDays] = useState('30');
  const [city, setCity] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [partner, setPartner] = useState('');
  const [claimed, setClaimed] = useState('');
  const [orderBy, setOrderBy] = useState<MerchantInterestSort>('INTEREST');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const filters: MerchantInterestQuery = useMemo(() => {
    const { from, to } = periodOf(Number(days));
    return {
      from,
      to,
      ...(city ? { city } : {}),
      ...(categoryId ? { categoryId } : {}),
      partner: parseTriState(partner),
      claimed: parseTriState(claimed),
    };
  }, [days, city, categoryId, partner, claimed]);

  const summary = useAsync(
    () => backofficeApi.getMerchantInterestSummary(filters),
    [filters]
  );

  const ranking = useAsync(
    () => backofficeApi.listMerchantInterest({ ...filters, orderBy, page, size: pageSize }),
    [filters, orderBy, page, pageSize]
  );

  /**
   * Qualquer troca de filtro volta para a primeira página.
   *
   * Sem isto, filtrar estando na página 4 mostra uma lista vazia que parece
   * "não há resultado" - e o operador conclui o oposto do que o dado diz.
   */
  const changeFilter = (apply: () => void) => {
    apply();
    setPage(0);
  };

  const cityItems: ComboItem[] = useMemo(
    () =>
      (summary.data?.cities ?? [])
        .filter((row) => row.city)
        .map((row) => ({ id: row.city as string, label: `${row.city} (${formatNumber(row.events)})` })),
    [summary.data]
  );

  const categoryItems: ComboItem[] = useMemo(
    () =>
      (summary.data?.categories ?? [])
        .filter((row) => row.categoryId && row.categoryName)
        .map((row) => ({
          id: row.categoryId as string,
          label: `${row.categoryName} (${formatNumber(row.events)})`,
        })),
    [summary.data]
  );

  const cityBars = (summary.data?.cities ?? []).slice(0, MAX_BARS).map((row) => ({
    nome: cityLabel(row.city),
    Interesse: row.events,
  }));

  const categoryBars = (summary.data?.categories ?? []).slice(0, MAX_BARS).map((row) => ({
    nome: row.categoryName ?? 'Sem categoria',
    Interesse: row.events,
  }));

  const rows = ranking.data?.content ?? [];
  const totals = summary.data?.totals;

  return (
    <>
      <PageHeader
        title="Interesse por empresa"
        subtitle={
          summary.data
            ? `Onde há procura no guia, entre ${formatDate(summary.data.from)} e ${formatDate(summary.data.to)}.`
            : 'Onde há procura no guia, e para quem vale a pena ligar.'
        }
      />

      <Toolbar>
        <FilterField>
          <Select
            label="Período"
            options={PERIOD_OPTIONS}
            value={days}
            onChange={(e) => changeFilter(() => setDays(e.target.value))}
          />
        </FilterField>
        <FilterField>
          <SearchableComboBox
            label="Cidade"
            items={cityItems}
            valueId={city}
            onChange={(id) => changeFilter(() => setCity(id))}
            placeholder="Todas as cidades"
            loading={summary.loading}
            noResultsText="Nenhuma cidade com procura no período"
          />
        </FilterField>
        <FilterField>
          <SearchableComboBox
            label="Categoria"
            items={categoryItems}
            valueId={categoryId}
            onChange={(id) => changeFilter(() => setCategoryId(id))}
            placeholder="Todas as categorias"
            loading={summary.loading}
            noResultsText="Nenhuma categoria com procura no período"
          />
        </FilterField>
        <FilterField>
          <Select
            label="Parceria no momento do evento"
            options={PARTNER_OPTIONS}
            value={partner}
            onChange={(e) => changeFilter(() => setPartner(e.target.value))}
          />
        </FilterField>
        <FilterField>
          <Select
            label="Reivindicação no momento do evento"
            options={CLAIM_OPTIONS}
            value={claimed}
            onChange={(e) => changeFilter(() => setClaimed(e.target.value))}
          />
        </FilterField>
        <FilterField>
          <Select
            label="Ordenação"
            options={ORDER_OPTIONS}
            value={orderBy}
            onChange={(e) => {
              setOrderBy(e.target.value as MerchantInterestSort);
              setPage(0);
            }}
          />
        </FilterField>
      </Toolbar>

      {/* O resumo carrega separado do ranking: paginar a lista não pode
          recarregar os gráficos, e um erro aqui não pode apagar a lista. */}
      <DataState loading={summary.loading} error={summary.error} onRetry={summary.reload}>
        {totals && (
          <>
            <Grid>
              <StatCard
                label="Empresas com procura"
                value={formatNumber(totals.merchants)}
                icon={<Store size={18} />}
                iconColor={theme.primary}
              />
              <StatCard
                label="Interesse no período"
                value={formatNumber(totals.totalEvents)}
                icon={<Sparkles size={18} />}
                iconColor={theme.info}
              />
              <StatCard
                label="Tentativas de contato"
                value={formatNumber(totals.contacts)}
                icon={<MousePointerClick size={18} />}
                iconColor={theme.accent}
              />
              <StatCard
                label="Antes de haver parceria"
                value={formatNumber(totals.prePartnerEvents)}
                icon={<Building2 size={18} />}
                iconColor={theme.success}
              />
            </Grid>

            <Split>
              <Card>
                <CardHeader>
                  <h3>Interesse por cidade</h3>
                </CardHeader>
                <CardBody>
                  {cityBars.length === 0 ? (
                    <Muted>Sem procura registrada no período.</Muted>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={cityBars}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                        <XAxis dataKey="nome" tick={{ fontSize: 12, fill: theme.textMuted }} />
                        <YAxis tick={{ fontSize: 12, fill: theme.textMuted }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="Interesse" fill={theme.primary} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h3>Interesse por categoria</h3>
                </CardHeader>
                <CardBody>
                  {categoryBars.length === 0 ? (
                    <Muted>Sem procura registrada no período.</Muted>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={categoryBars}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                        <XAxis dataKey="nome" tick={{ fontSize: 12, fill: theme.textMuted }} />
                        <YAxis tick={{ fontSize: 12, fill: theme.textMuted }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="Interesse" fill={theme.info} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardBody>
              </Card>
            </Split>
          </>
        )}
      </DataState>

      <DataState
        loading={ranking.loading}
        error={ranking.error}
        onRetry={ranking.reload}
        isEmpty={rows.length === 0}
        emptyTitle="Nenhuma empresa com procura neste recorte"
        emptyDescription="Troque o período ou tire um filtro. A coleta começou com o card 230: não há dado anterior a ela."
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Cidade</th>
                <th>Categoria</th>
                <NumericHead>Interesse</NumericHead>
                <NumericHead>Visualizações</NumericHead>
                <NumericHead>Contatos</NumericHead>
                <NumericHead>Salvamentos</NumericHead>
                <NumericHead>Antes da parceria</NumericHead>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.merchantId}>
                  <td>
                    <strong>{row.merchantName}</strong>
                  </td>
                  <td>{row.city ?? <Muted>-</Muted>}</td>
                  <td>{row.categoryName ?? <Muted>-</Muted>}</td>
                  <Numeric>
                    <strong>{formatNumber(row.totalEvents)}</strong>
                  </Numeric>
                  <Numeric>{formatNumber(row.views)}</Numeric>
                  <Numeric>{formatNumber(row.contacts)}</Numeric>
                  <Numeric>{formatNumber(row.saves)}</Numeric>
                  <Numeric>
                    {/* Só destaca quando houve procura antes da parceria: com
                        zero, o `success` pintaria de verde a ausência de
                        argumento comercial. */}
                    <Badge tone={row.prePartnerEvents > 0 ? 'success' : 'secondary'}>
                      {formatNumber(row.prePartnerEvents)}
                    </Badge>
                  </Numeric>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {!ranking.error && (
        <Pagination
          currentPage={page + 1}
          totalPages={ranking.data?.totalPages ?? 0}
          totalItems={ranking.data?.totalElements ?? 0}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={ranking.loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      )}

      <Note>
        <Info size={15} />
        <span>
          Os números são de <strong>eventos</strong>, não de pessoas: quem abre a página e depois
          clica no WhatsApp conta nas duas colunas. Cidade, categoria e o estado da relação
          comercial são os do <strong>momento do evento</strong> - uma empresa que mudou de
          categoria não reescreve o próprio passado aqui. Nada nesta tela identifica visitante: a
          coleta não guarda IP, conta nem telefone.
        </span>
      </Note>
    </>
  );
};

export default MerchantInterestPage;
